import {
  getMeasurementIntervalDisplayText,
  translateMeasurementIntervalOptions,
} from 'home/Machines/MachineBuilder/constants/NodeDetails.constants';

const SAMPLING_OPTION_REGEX = /^(\d+)\s+lines\s+upto\s+(\d+)\s+Hz$/i;
const HIGH_PASS_CUTOFF_REGEX = /^([\d.]+)\s*Hz$/i;

const MEASUREMENT_INTERVAL_FIELDS = new Set([
  'reporting_interval',
  'aggressive_measurement_timeout',
  'overall_measurement_interval',
  'tx_reporting_interval',
]);

const isSamplingField = fieldName =>
  typeof fieldName === 'string' && fieldName.includes('sampling');

const isMeasurementIntervalField = fieldName =>
  MEASUREMENT_INTERVAL_FIELDS.has(fieldName);

const EVENT_SENSOR_TYPE_KEYS = {
  Vibration: 'vibration',
  Temperature: 'temperature',
  Magnetometer: 'magnetometer',
};

const EVENT_METRIC_TYPE_KEYS = {
  'Accel RMS': 'accelRms',
  'Accel Peak': 'accelPeak',
  'Accel P2P': 'accelP2p',
  'Velocity RMS': 'velocityRms',
  'Velocity Peak': 'velocityPeak',
  'Velocity P2P': 'velocityP2p',
  'Magnetic Flux': 'magneticFlux',
  Speed: 'speed',
  'Acceleration RMS': 'accelRms',
  'Acceleration Peak': 'accelPeak',
  'Acceleration Peak-to-Peak': 'accelP2p',
  'Velocity Peak-to-Peak': 'velocityP2p',
  Temperature: 'temperature',
  Overall: 'overall',
};

const EVENT_ACTION_KEYS = {
  'Immediate Upload': 'immediateUpload',
  'Schedule Change': 'scheduleChange',
};

const EVENT_LOGIC_KEYS = {
  AND: 'and',
  OR: 'or',
};

const MEASUREMENT_SCHEDULE_TYPE_FIELDS = new Set([
  'spectrum_measurement_type',
  'measurement_settings_type',
]);

const isMeasurementScheduleTypeField = fieldName =>
  MEASUREMENT_SCHEDULE_TYPE_FIELDS.has(fieldName);

export const translateMeasurementScheduleTypeOption = (t, option) => {
  if (!option) return '';
  const normalizedValue = String(option.value || option.text || '').toLowerCase();
  if (normalizedValue === 'state_based' || normalizedValue === 'state based') {
    return t('sensors.detail.stateBased', { defaultValue: 'State Based' });
  }
  if (normalizedValue === 'schedule_based' || normalizedValue === 'schedule based') {
    return t('sensors.detail.scheduleBased', { defaultValue: 'Schedule Based' });
  }
  return option.text;
};

export const translateScheduleTypeLabel = (t, stateBasedMeasurement, scheduleType) => {
  const isStateBased =
    Boolean(stateBasedMeasurement) || scheduleType === 'state_based';
  return t(`sensors.detail.${isStateBased ? 'stateBased' : 'scheduleBased'}`, {
    defaultValue: isStateBased ? 'State Based' : 'Schedule Based',
  });
};

export const translateFsRangeLabel = (t, value) => {
  if (value == null || value === '') return '--';
  return t('sensors.detail.fsRangeValue', { value, defaultValue: `± ${value}g` });
};

// Magnetometer ranges are gauss (capital G), not the accelerometer's g-force.
export const translateMagnetometerFsRangeLabel = (t, value) => {
  if (value == null || value === '') return '--';
  return t('sensors.detail.magnetometerFsRangeValue', { value, defaultValue: `± ${value} G` });
};

export const translateSamplingOptionText = (t, text) => {
  if (!text || typeof text !== 'string') return text;
  const match = text.match(SAMPLING_OPTION_REGEX);
  if (!match) return text;
  return t('sensors.detail.samplingOption', {
    lines: match[1],
    hz: match[2],
    defaultValue: text,
  });
};

export const translateHighPassCutoffText = (t, text) => {
  if (!text || typeof text !== 'string') return text;
  const match = text.match(HIGH_PASS_CUTOFF_REGEX);
  if (!match) return text;
  return t('sensors.detail.highPassCutoffValue', {
    value: match[1],
    defaultValue: text,
  });
};

export const translateSensorSelectOptionText = (t, fieldName, option) => {
  if (!option) return '';
  const { text } = option;

  if (isMeasurementScheduleTypeField(fieldName)) {
    return translateMeasurementScheduleTypeOption(t, option);
  }

  if (isMeasurementIntervalField(fieldName)) {
    return getMeasurementIntervalDisplayText(t, option);
  }

  if (isSamplingField(fieldName)) {
    return translateSamplingOptionText(t, text);
  }

  if (fieldName && fieldName.includes('high_pass_filter_cutoff')) {
    return translateHighPassCutoffText(t, text);
  }

  return text;
};

export const translateSensorSelectOptions = (t, fieldName, options) => {
  if (!Array.isArray(options)) return options;

  if (isMeasurementIntervalField(fieldName)) {
    return translateMeasurementIntervalOptions(t, options);
  }

  return options.map(opt => ({
    ...opt,
    text: translateSensorSelectOptionText(t, fieldName, opt),
  }));
};

export const translateEventDetectionSensorType = (t, name) => {
  const key = EVENT_SENSOR_TYPE_KEYS[name];
  if (!key) return name;
  return t(`sensors.eventDetection.metrics.${key}`, { defaultValue: name });
};

export const translateEventDetectionMetricType = (t, name) => {
  const key = EVENT_METRIC_TYPE_KEYS[name];
  if (!key) return name;
  return t(`sensors.eventDetection.metricTypes.${key}`, { defaultValue: name });
};

export const translateEventDetectionAction = (t, name) => {
  const key = EVENT_ACTION_KEYS[name];
  if (!key) return name;
  return t(`sensors.eventDetection.actions.${key}`, { defaultValue: name });
};

export const translateEventDetectionLogic = (t, logic) => {
  const key = EVENT_LOGIC_KEYS[logic];
  if (!key) return logic;
  return t(`sensors.eventDetection.logic.${key}`, { defaultValue: logic });
};
